import { forwardRef, useEffect, useRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { Check, Minus } from 'lucide-react';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  label?: ReactNode;
  hint?: ReactNode;
  /** Some but not all children selected — the header checkbox of a table. */
  indeterminate?: boolean;
  size?: 'sm' | 'md';
  containerClassName?: string;
}

/**
 * A checkbox with a real input underneath, so form semantics, the space key and
 * label clicks all work without being reimplemented. The box on screen is drawn from
 * a sibling element and the input itself is invisible but present.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  {
    label,
    hint,
    indeterminate = false,
    size = 'md',
    className,
    containerClassName,
    disabled,
    ...rest
  },
  ref,
) {
  const inner = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (inner.current) inner.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const box = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
  const icon = size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3';

  return (
    <label
      className={clsx(
        'group inline-flex min-w-0 items-start gap-2',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        containerClassName,
      )}
    >
      <span className="relative flex shrink-0 items-center justify-center pt-0.5">
        <input
          ref={(node) => {
            inner.current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
          }}
          type="checkbox"
          disabled={disabled}
          aria-checked={indeterminate ? 'mixed' : undefined}
          className={clsx('peer absolute h-0 w-0 opacity-0', className)}
          {...rest}
        />
        <span
          aria-hidden
          className={clsx(
            box,
            'flex items-center justify-center rounded-[4px] border border-line-strong bg-surface',
            'transition-colors duration-150',
            'group-hover:border-faint',
            'peer-checked:border-accent peer-checked:bg-accent peer-checked:text-accent-ink',
            'peer-indeterminate:border-accent peer-indeterminate:bg-accent peer-indeterminate:text-accent-ink',
            'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent',
            // The tick lives inside this box, so its visibility has to be driven from
            // here — `peer-*` only reaches siblings, not their descendants.
            'peer-checked:[&_svg]:opacity-100',
          )}
        >
          {indeterminate ? (
            <Minus className={icon} strokeWidth={3} />
          ) : (
            <Check className={clsx(icon, 'opacity-0')} strokeWidth={3} />
          )}
        </span>
      </span>

      {label || hint ? (
        <span className="min-w-0">
          {label ? (
            <span className={clsx('block text-ink', size === 'sm' ? 'text-micro' : 'text-body')}>
              {label}
            </span>
          ) : null}
          {hint ? <span className="block text-micro text-muted">{hint}</span> : null}
        </span>
      ) : null}
    </label>
  );
});
